// Что пользователь уже прошёл: тур и пункты чек-листа.
//
// Хранится в профиле на сервере — иначе менеджер, пересевший за соседний
// компьютер, получал бы тур заново. В браузере держим копию: она отвечает
// мгновенно, пока идёт запрос, и выручает, если сервер недоступен.
//
// Обучение — не та вещь, из-за которой стоит показывать ошибку: любой сбой
// запроса здесь гасится, и человек просто работает дальше.

import getOnboarding from '../../api/onboarding/getOnboarding';
import saveOnboarding from '../../api/onboarding/saveOnboarding';
import resetOnboardingOnServer from '../../api/onboarding/resetOnboarding';

import { CHECKLIST_ITEMS } from './onboardingSteps';

const PREFIX = 'onboarding';

const EMPTY_STATE = { tourSeen: false, checklist: [], checklistHidden: false };

// Копия в браузере лежит отдельно на каждого: за одним компьютером в салоне
// работает вся смена.
const keyFor = (userId, name) => `${PREFIX}:${userId || 'anonymous'}:${name}`;

// localStorage недоступен в приватном окне и при запрете на сайт — тогда
// просто ведём себя как «ничего не сохранено», а не роняем страницу.
const read = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const write = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
};

const remove = (key) => {
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
};

const cleanChecklist = (items) => {
  if (!Array.isArray(items)) return [];
  return CHECKLIST_ITEMS.filter((item) => items.includes(item));
};

export const readLocalState = (userId) => {
  const raw = read(keyFor(userId, 'state'));
  if (!raw) return { ...EMPTY_STATE };

  try {
    const parsed = JSON.parse(raw);
    return {
      tourSeen: !!parsed?.tourSeen,
      checklist: cleanChecklist(parsed?.checklist),
      checklistHidden: !!parsed?.checklistHidden,
    };
  } catch (e) {
    // Пришёл мусор или ключ из прошлой версии — считаем, что ничего не пройдено.
    return { ...EMPTY_STATE };
  }
};

export const writeLocalState = (userId, state) => write(keyFor(userId, 'state'), JSON.stringify(state));

export const clearLocalState = (userId) => remove(keyFor(userId, 'state'));

// Состояние при входе. Сервер не ответил — берём копию из браузера, чтобы не
// показать тур человеку, который его уже проходил.
export const loadOnboardingState = async (userId) => {
  try {
    const { data } = await getOnboarding();

    const state = {
      tourSeen: !!data?.tourSeen,
      checklist: cleanChecklist(data?.checklist),
      checklistHidden: !!data?.checklistHidden,
    };

    writeLocalState(userId, state);
    return state;
  } catch (e) {
    return readLocalState(userId);
  }
};

// Сохраняем сразу и в браузер, и на сервер: ответа ждать некому, экран уже
// перерисовался.
export const persistOnboardingState = async (userId, state) => {
  writeLocalState(userId, state);

  try {
    await saveOnboarding(state);
    return true;
  } catch (e) {
    return false;
  }
};

export const resetOnboardingState = async (userId) => {
  clearLocalState(userId);

  try {
    await resetOnboardingOnServer();
    return true;
  } catch (e) {
    return false;
  }
};
